import { useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { createContexts } from '@/js/libs/create-contexts';
import '@/js/libs/framework/log';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useState$, useSubscribe } from '@/js/libs/observable';
import { ModalDialog } from '@/js/libs/ui/src/V2';

interface CommonConfirmationProps {
  message: ReactNode;
  title?: string;
  confirmButtonText?: string;
  isError?: boolean;
  /** Highlighted block under the message, for a caveat the user has to notice before confirming. */
  notification?: ComponentProps<typeof ModalDialog>['notification'];
  onActionClick?: never;
}
interface ConfirmationPropsWithOnConfirm<T> extends CommonConfirmationProps {
  onConfirm: () => Promise<T>;
  /**
   * Called when `onConfirm` throws. `ask` itself never rejects — it resolves `false` after this callback.
   */
  onReject?: (error: unknown) => void | Promise<void>;
}

type ConfirmationAskProps<T> = CommonConfirmationProps | ConfirmationPropsWithOnConfirm<T>;

export const { useConfirmationModalContext } = createContexts({
  ConfirmationModal: () => {
    const [modalProps, setModalProps] = useState<ConfirmationAskProps<unknown>>();
    const [pending$, setPending] = useState$<boolean>(false);

    const cancelRef = useRef<(() => void) | undefined>();
    const confirmRef = useRef<(() => Promise<void>) | undefined>();

    const modalState = useModalState({ onClose: cancelRef.current });

    const onActionClick = useCallbackRef(async () => {
      try {
        setPending(true);
        const onConfirm = confirmRef.current;
        if (onConfirm) await onConfirm();
      } finally {
        setPending(false);
        modalState.close();
      }
    });

    return {
      modalState,
      pending$,
      modalProps,
      onActionClick,
      open: ({
        props,
        onConfirm,
        onCancel,
      }: {
        props: ConfirmationAskProps<unknown>;
        onConfirm: () => Promise<void>;
        onCancel: () => void;
      }) => {
        setModalProps(props);
        confirmRef.current = onConfirm;
        cancelRef.current = onCancel;
        modalState.open();
      },
    };
  },
});

const Modal = () => {
  const [t] = useTranslation(['backend', 'common', 'messages']);
  const { modalState, modalProps, pending$, onActionClick } = useConfirmationModalContext();
  const { message, title, confirmButtonText, isError, notification } = modalProps ?? {};

  const pending = useSubscribe(pending$);

  return (
    modalState.isMounted && (
      <ModalDialog
        state={modalState}
        title={title ?? t('common:CONFIRM')}
        text={message}
        notification={notification}
        onActionButtonClick={onActionClick}
        cancelButtonText={t('common:CANCEL')}
        actionButtonText={confirmButtonText ?? t('common:YES_LABEL')}
        isPendingAction={pending}
        error={isError ?? false}
      />
    )
  );
};

/**
 * Ask the user for confirmation without extra modal wiring.
 * `.ask` always resolves after the modal closes; it never rejects.
 * - Confirmed, no `onConfirm` → `true`
 * - Confirmed, `onConfirm` succeeded → `onConfirm`'s result
 * - Dismissed → `false`
 * - `onConfirm` threw → `onReject` (if given), then `false`
 * The modal stays open until `onConfirm` settles.
 * @example
 * const confirm = useConfirmation();
 * // Simple
 * await confirm.ask({ message: 'Are you sure?' });
 *
 * // Regular
 * await confirm.ask({
 *   message: 'Are you sure?',
 *   onConfirm: async () => {
 *     await doSomething();
 *   },
 * });
 *
 * // Advanced
 * const wasConfirmed = await confirm.ask({
 *   message: 'Are you sure?',
 *   title: 'Confirmation',
 *   confirmButtonText: 'Confirm',
 *   isError: true,
 *   onConfirm: async () => {
 *     await doSomethingAfter();
 *     return true;
 *   },
 *   onReject: (error) => {
 *     // optional: onConfirm threw (ask does not rethrow)
 *   },
 * });
 */
type AskReturn<P> = P extends { onConfirm: () => Promise<infer T> } ? Promise<T | false> : Promise<boolean>;

type AskCb = {
  <T extends ConfirmationAskProps<unknown>>(props: T): AskReturn<T>;
};

export function useConfirmation() {
  const modalCtx = useConfirmationModalContext();

  const ask = useCallbackRef(
    ((props) =>
      new Promise<unknown | false>((resolve) => {
        modalCtx.open({
          props,
          onConfirm: async () => {
            if (!('onConfirm' in props)) {
              resolve(true);
              return;
            }

            const { onConfirm, onReject } = props;
            try {
              resolve(await onConfirm());
            } catch (error) {
              try {
                await onReject?.(error);
              } catch (rejectError) {
                console.dev.tag('useConfirmation').error(rejectError, 'onReject threw');
              } finally {
                resolve(false);
              }
            }
          },
          onCancel: () => resolve(false),
        });
      })) as AskCb,
  );

  return { ask };
}

useConfirmation.Modal = Modal;
